"use client";

import { useEffect, useState, useRef } from "react";
import Icon from "@/components/ui/AppIcon";


interface TimelineStep {
  id: string;
  phase: string;
  title: string;
  description: string;
  icon: string;
  duration: string;
  details: string[];
}

const ProcessTimeline = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const timeline: TimelineStep[] = [
    {
      id: "timeline_discovery",
      phase: "Phase 1",
      title: "Discovery & Requirements",
      description: "Understanding your needs and specifications",
      icon: "MagnifyingGlassIcon",
      duration: "",
      details: [
        "Initial consultation call",
        "Product specification review",
        "Volume and delivery requirements",
        "Quality standards alignment",
      ],
    },
    {
      id: "timeline_sourcing",
      phase: "Phase 2",
      title: "Sourcing & Quotation",
      description: "Providing the most competitive origin and pricing",
      icon: "CurrencyDollarIcon",
      duration: "Days 1-2",
      details: [
        "Global supplier network search",
        "Price comparison and negotiation",
        "Quality certification verification",
        "Detailed quotation with breakdown",
      ],
    },
    {
      id: "timeline_contracting",
      phase: "Phase 3",
      title: "Contract & Payment",
      description: "Secure agreements and flexible payment terms",
      icon: "DocumentCheckIcon",
      duration: "Days 2-3",
      details: [
        "Structuring in line with client's working capital needs and market appetite for bank instrument such as LCs",
        "Escrow service activation",
        "Contract execution and initiation of supply",
      ],
    },
    {
      id: "timeline_quality",
      phase: "Phase 4",
      title: "Quality Inspection",
      description: "Third-party testing and certification",
      icon: "ShieldCheckIcon",
      duration: "Days 3-5",
      details: [
        "Pre-shipment inspection",
        "Lab testing and analysis",
        "Quality certificate issuance",
        "Sample approval process",
      ],
    },
    {
      id: "timeline_logistics",
      phase: "Phase 5",
      title: "Logistics & Shipping",
      description: "Coordinated transportation from origin to destination",
      icon: "TruckIcon",
      duration: "Days 5-30",
      details: [
        "Freight booking and scheduling",
        "Insurance coverage",
      ],
    },
    {
      id: "timeline_delivery",
      phase: "Phase 6",
      title: "Delivery & Follow-up",
      description: "Final delivery confirmation and ongoing support",
      icon: "CheckCircleIcon",
      duration: "Day 30+",
      details: [
        "Delivery confirmation",
        "Quality verification at destination",
        "Payment settlement",
        "Post-delivery support",
      ],
    },
  ];

  return (
    <section data-component-id="src/app/services/components/ProcessTimeline.tsx:123:4" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="123" data-component-end-line="216" data-component-file="ProcessTimeline.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-24%20bg-gradient-to-b%20from-muted%2F30%20to-background%20relative%20overflow-hidden%22%7D"
      ref={sectionRef}
      className="py-24 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div data-component-id="src/app/services/components/ProcessTimeline.tsx:128:6" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="128" data-component-end-line="135" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20opacity-%5B0.02%5D%22%7D"
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(46, 125, 50, 0.1) 2px, transparent 2px),
                           linear-gradient(90deg, rgba(46, 125, 50, 0.1) 2px, transparent 2px)`,
          backgroundSize: "40px 40px",
        }}
      />

      <div data-component-id="src/app/services/components/ProcessTimeline.tsx:137:6" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="137" data-component-end-line="215" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container-custom%20relative%20z-10%22%7D" className="container-custom relative z-10">
        {/* Section Header */}
        <div data-component-id="src/app/services/components/ProcessTimeline.tsx:139:8" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="139" data-component-end-line="154" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-20%22%7D" className="text-center mb-20">
          <h2 data-component-id="src/app/services/components/ProcessTimeline.tsx:140:10" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="140" data-component-end-line="146" data-component-file="ProcessTimeline.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22textContent%22%3A%22Our%20Trading%22%7D"
            className={`text-4xl md:text-5xl font-heading font-bold mb-6 transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Our Trading <span data-component-id="src/app/services/components/ProcessTimeline.tsx:145:24" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="145" data-component-end-line="145" data-component-file="ProcessTimeline.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gradient-primary%22%2C%22textContent%22%3A%22Process%22%7D" className="text-gradient-primary">Process</span>
          </h2>
          <p data-component-id="src/app/services/components/ProcessTimeline.tsx:147:10" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="147" data-component-end-line="153" data-component-file="ProcessTimeline.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22A%20transparent%2C%20step-by-step%20journey%20from%20inquiry%20to%20delivery%22%7D"
            className={`text-lg text-muted-foreground max-w-2xl mx-auto transition-all duration-1000 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            A transparent, step-by-step journey from inquiry to delivery
          </p>
        </div>

        {/* Timeline */}
        <div data-component-id="src/app/services/components/ProcessTimeline.tsx:157:8" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="157" data-component-end-line="214" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%22%7D" className="max-w-4xl mx-auto">
          {timeline.map((step, index) => (
            <div data-component-id="src/app/services/components/ProcessTimeline.tsx:159:12" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="159" data-component-end-line="212" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={step.id}
              className={`relative transition-all duration-1000 ${
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div data-component-id="src/app/services/components/ProcessTimeline.tsx:166:14" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="166" data-component-end-line="211" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-8%20mb-12%22%7D" className="flex gap-8 mb-12">
                {/* Timeline Line & Icon */}
                <div data-component-id="src/app/services/components/ProcessTimeline.tsx:168:16" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="168" data-component-end-line="177" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%22%7D" className="flex flex-col items-center">
                  {/* Icon */}
                  <div data-component-id="src/app/services/components/ProcessTimeline.tsx:170:18" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="170" data-component-end-line="172" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20rounded-full%20bg-primary%20text-primary-foreground%20flex%20items-center%20justify-center%20shadow-lg%20z-10%20relative%22%7D" className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg z-10 relative">
                    <Icon data-component-id="src/app/services/components/ProcessTimeline.tsx:171:20" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="171" data-component-end-line="171" data-component-file="ProcessTimeline.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={step.icon as any} size={28} variant="solid" />
                  </div>
                  {/* Vertical Line */}
                  {index < timeline.length - 1 && (
                    <div data-component-id="src/app/services/components/ProcessTimeline.tsx:175:20" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="175" data-component-end-line="175" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-0.5%20h-full%20bg-gradient-to-b%20from-primary%20to-primary%2F20%20mt-4%22%7D" className="w-0.5 h-full bg-gradient-to-b from-primary to-primary/20 mt-4" />
                  )}
                </div>

                {/* Content */}
                <div data-component-id="src/app/services/components/ProcessTimeline.tsx:180:16" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="180" data-component-end-line="210" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20pb-12%22%7D" className="flex-1 pb-12">
                  {/* Phase Badge */}
                  <div data-component-id="src/app/services/components/ProcessTimeline.tsx:182:18" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="182" data-component-end-line="185" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-2%20px-3%20py-1%20rounded-full%20bg-primary%2F10%20border%20border-primary%2F20%20text-xs%20font-semibold%20text-primary%20mb-3%22%7D" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary mb-3">
                    {step.phase}
                    <span data-component-id="src/app/services/components/ProcessTimeline.tsx:184:20" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="184" data-component-end-line="184" data-component-file="ProcessTimeline.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22%E2%80%A2%22%7D" className="text-muted-foreground">• {step.duration}</span>
                  </div>

                  {/* Title & Description */}
                  <h3 data-component-id="src/app/services/components/ProcessTimeline.tsx:188:18" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="188" data-component-end-line="190" data-component-file="ProcessTimeline.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-2xl%20font-heading%20font-bold%20text-foreground%20mb-2%22%7D" className="text-2xl font-heading font-bold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p data-component-id="src/app/services/components/ProcessTimeline.tsx:191:18" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="191" data-component-end-line="193" data-component-file="ProcessTimeline.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20mb-4%20leading-relaxed%22%7D" className="text-muted-foreground mb-4 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Details */}
                  <div data-component-id="src/app/services/components/ProcessTimeline.tsx:196:18" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="196" data-component-end-line="209" data-component-file="ProcessTimeline.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22glass-panel%20rounded-xl%20p-6%22%7D" className="glass-panel rounded-xl p-6">
                    <ul data-component-id="src/app/services/components/ProcessTimeline.tsx:197:20" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="197" data-component-end-line="208" data-component-file="ProcessTimeline.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
                      {step.details.map((detail, idx) => (
                        <li data-component-id="src/app/services/components/ProcessTimeline.tsx:199:24" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="199" data-component-end-line="206" data-component-file="ProcessTimeline.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22flex%20items-start%20gap-3%20text-sm%22%7D" key={idx} className="flex items-start gap-3 text-sm">
                          <Icon data-component-id="src/app/services/components/ProcessTimeline.tsx:200:26" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="200" data-component-end-line="204" data-component-file="ProcessTimeline.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%2C%22className%22%3A%22text-success%20mt-0.5%20flex-shrink-0%22%7D"
                            name="CheckIcon"
                            size={16}
                            className="text-success mt-0.5 flex-shrink-0"
                          />
                          <span data-component-id="src/app/services/components/ProcessTimeline.tsx:205:26" data-component-path="src/app/services/components/ProcessTimeline.tsx" data-component-line="205" data-component-end-line="205" data-component-file="ProcessTimeline.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-muted-foreground%22%7D" className="text-muted-foreground">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;